import * as AlertDialog from '@radix-ui/react-alert-dialog';
import React, { useCallback, useEffect, useImperativeHandle } from 'react';

import './AlertDialog.less';

type RefProps = {
  open: () => void;
};

type JSXFnCallback = ((props: { closeDialog: () => void }) => React.JSX.Element) | null;
type JSXFnButtonCallback = ((props: { openDialog: () => void }) => React.JSX.Element) | null;

type Props = {
  title: string;
  description?: string;

  closeOnClickedOutside?: boolean;

  cancelText?: string;
  cancelButton?: JSXFnCallback;
  hideCancel?: boolean;

  acceptText?: string;
  acceptCallback?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, close: () => void) => void;
  acceptButton?: JSXFnCallback;
  hideAccept?: boolean;

  body?: JSXFnCallback;

  /**
   * @description This is the text that will be displayed on the button that triggers the dialog
   */
  buttonTxt?: string;
  buttonElement?: React.JSX.Element | JSXFnButtonCallback;

  /**
   * @description Extra actions to be added to the bottom of the dialog. This should be an array of buttons
   */
  extraButtons?: JSXFnCallback[];

  /**
   * @description Custom styles for the overlay
   */
  overlayStyle?: React.CSSProperties;

  size?: 'small' | 'medium' | 'large';
  onOpenDialog?: () => void;
  stopAllPropagation?: boolean;
  disabled?: boolean;
};

const AlertDialogComponent = (
  { title, description, closeOnClickedOutside = true, ...props }: Props,
  forwardRef: React.ForwardedRef<RefProps>
) => {
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (open === true) {
      props?.onOpenDialog?.();
    }
  }, [open]);

  useImperativeHandle(
    forwardRef,
    () => ({
      open: () => {
        setOpen(true);
      },
    }),
    [setOpen, forwardRef]
  );

  const closeDialog = () => {
    setOpen(false);
  };

  const defaultOnClickHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    props?.stopAllPropagation && e.stopPropagation();
  };

  const BtnEl = useCallback(
    React.forwardRef(() => {
      if (props?.buttonElement) {
        if (typeof props.buttonElement === 'function') return props.buttonElement({ openDialog: () => setOpen(true) });

        // add onClick handler to buttonElement
        return React.cloneElement(props.buttonElement, {
          disabled: props?.disabled,
          onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (!props?.disabled) {
              defaultOnClickHandler(e);
              setOpen(true);
            }
          },
        });
      }
      if (props?.buttonTxt)
        return (
          <button
            type="button"
            className="button ui-alertdialog--trigger z-btn-1"
            onClick={(e) => {
              defaultOnClickHandler(e);
              if (!props?.disabled) {
                setOpen(true);
              }
            }}
            disabled={props?.disabled}
          >
            {props.buttonTxt}
          </button>
        );

      return <></>;
    }),
    [props?.buttonElement, props?.buttonTxt, defaultOnClickHandler, props?.disabled]
  );

  const AcceptButton = useCallback(
    React.forwardRef(() => {
      if (props?.hideAccept) return <></>;

      if (props?.acceptButton)
        return <AlertDialog.Action asChild>{props.acceptButton({ closeDialog })}</AlertDialog.Action>;

      return (
        <AlertDialog.Action asChild>
          <button
            type="button"
            className="button button-content--action z-btn-1 green"
            onClick={(e) => {
              props?.acceptCallback?.(e, closeDialog);
              defaultOnClickHandler(e);
            }}
          >
            {props?.acceptText ?? 'Yes'}
          </button>
        </AlertDialog.Action>
      );
    }),
    [props?.hideAccept, props?.acceptButton, props?.acceptText, props?.acceptCallback, defaultOnClickHandler]
  );

  const CancelButton = useCallback(
    React.forwardRef(() => {
      if (props?.hideCancel) return <></>;

      if (props?.cancelButton)
        return <AlertDialog.Cancel asChild>{props.cancelButton({ closeDialog })}</AlertDialog.Cancel>;

      return (
        <AlertDialog.Cancel asChild>
          <button type="button" className="button button-content--cancel z-btn-1" onClick={defaultOnClickHandler}>
            {props?.cancelText ?? 'Cancel'}
          </button>
        </AlertDialog.Cancel>
      );
    }),
    [props?.hideCancel, props?.cancelButton, props?.cancelText, defaultOnClickHandler, closeDialog]
  );

  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      <AlertDialog.Trigger asChild>
        <BtnEl />
      </AlertDialog.Trigger>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className="ui-alertdialog--overlay"
          style={props?.overlayStyle}
          onClick={(e) => {
            defaultOnClickHandler(e);
            closeOnClickedOutside && setOpen(false);
          }}
        />
        <AlertDialog.Content
          className={`ui-alertdialog--content ${props?.size ?? 'small'}`}
          onClick={defaultOnClickHandler}
        >
          <AlertDialog.Title className="ad-content--title">{title}</AlertDialog.Title>
          {description && (
            <AlertDialog.Description className="ad-content--description">{description}</AlertDialog.Description>
          )}
          <div className="ad-content--body">{props?.body?.({ closeDialog })}</div>

          <div className="ad-content--action-btns">
            <CancelButton />
            {props?.extraButtons
              ?.filter((el) => !!el)
              ?.map((ExtraButtonElement, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: Not enough context to provide a unique key
                <AlertDialog.Action key={idx} asChild onClick={defaultOnClickHandler}>
                  <ExtraButtonElement closeDialog={closeDialog} />
                </AlertDialog.Action>
              ))}
            <AcceptButton />
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

const AlertDialogRef = React.forwardRef(AlertDialogComponent);

const useAlertDialogRef = () => {
  return React.useRef<RefProps>(null);
};

export { AlertDialogRef as AlertDialog, useAlertDialogRef };
export type { RefProps as AlertDialogRefProps, Props as AlertDialogProps };
export default AlertDialogRef;
